import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Button, Space, Empty, Badge, message } from 'antd';
import { AimOutlined, ExpandOutlined, ReloadOutlined } from '@ant-design/icons';
import { renderToString } from 'react-dom/server';
import { PiCalendarBlank, PiCompass, PiMapPin } from 'react-icons/pi';
import type { Photo_Base } from '~/common/typescript/apis/PhotoAPIs';
import { useFreePhotoMapClusters } from '../hooks/useFreePhotoMapClusters';
import { formatPhotoDate } from '../utils/freePhotoUtils';
import { getAxiosInstance } from '~/common/react/utils/networkUtil';
import { API_URL_ADMIN } from '~/config/config';

interface FreePhotoMapViewProps {
  photos: Photo_Base[];
  onPhotoClick: (photo: Photo_Base) => void;
}

/**
 * Fetch a photo blob URL using the JWT-authenticated backend endpoint.
 * Returns a Promise that resolves to an object URL (caller must revoke it).
 */
const fetchAuthThumbnail = async (uuid: string, sourceType: string = 'free_photo'): Promise<string> => {
  const endpoint = sourceType === 'free_photo'
    ? '/api/geocollect/restore/download-free-photo-file'
    : '/api/geocollect/restore/download-file';
  const response = await getAxiosInstance().get(
    `${endpoint}/${uuid}`,
    { responseType: 'blob', baseURL: API_URL_ADMIN },
  );
  return URL.createObjectURL(response.data);
};

/**
 * Enhanced Map view component for displaying photos on a map with clustering
 */
export const FreePhotoMapView: React.FC<FreePhotoMapViewProps> = ({ photos, onPhotoClick }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const popupsRef = useRef<maplibregl.Popup[]>([]);
  const blobUrlsRef = useRef<string[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  
  const photoMarkers = useFreePhotoMapClusters(photos);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    try {
      map.current = new maplibregl.Map({
        container: mapContainer.current,
        style: {
          version: 8,
          sources: {
            'osm-tiles': {
              type: 'raster',
              tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
              tileSize: 256,
              attribution: '© OpenStreetMap contributors',
            },
          },
          layers: [
            {
              id: 'osm-tiles-layer',
              type: 'raster',
              source: 'osm-tiles',
              minzoom: 0,
              maxzoom: 19,
            },
          ],
        },
        center: [105.8342, 21.0278], // Hanoi default
        zoom: 10,
      });

      map.current.addControl(new maplibregl.NavigationControl(), 'top-right');
      map.current.addControl(new maplibregl.ScaleControl(), 'bottom-right');

      map.current.on('load', () => {
        setMapLoaded(true);
      });

      return () => {
        // Cleanup
        markersRef.current.forEach((marker) => marker.remove());
        popupsRef.current.forEach((popup) => popup.remove());
        blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        blobUrlsRef.current = [];
        map.current?.remove();
        map.current = null;
        setMapLoaded(false);
      };
    } catch (error) {
      console.error('Error initializing map:', error);
      message.error('Không thể khởi tạo bản đồ');
    }
  }, []);

  // Update markers when photos change
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Clear existing markers, popups and blob URLs
    markersRef.current.forEach((marker) => marker.remove());
    popupsRef.current.forEach((popup) => popup.remove());
    blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    markersRef.current = [];
    popupsRef.current = [];
    blobUrlsRef.current = [];

    if (photoMarkers.length === 0) return;

    // Add markers for each photo
    photoMarkers.forEach((markerData) => {
      const { photo, coordinates, compassHeading } = markerData;

      // Create custom marker element with rotation
      const el = document.createElement('div');
      el.className = 'photo-marker';
      el.style.cursor = 'pointer';
      
      // Outer container (doesn't rotate)
      const container = document.createElement('div');
      container.style.cssText = `
        width: 36px;
        height: 36px;
        position: relative;
      `;

      // Inner circle with arrow (rotates)
      const arrow = document.createElement('div');
      arrow.style.cssText = `
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
        border: 3px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        transform: rotate(${compassHeading}deg);
        transition: all 0.2s ease;
      `;

      arrow.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
          <path d="M12 2 L16 20 L12 17 L8 20 Z" />
        </svg>
      `;

      container.appendChild(arrow);
      el.appendChild(container);

      // Hover effect
      el.addEventListener('mouseenter', () => {
        arrow.style.transform = `rotate(${compassHeading}deg) scale(1.2)`;
        arrow.style.boxShadow = '0 4px 12px rgba(0,0,0,0.4)';
      });

      el.addEventListener('mouseleave', () => {
        arrow.style.transform = `rotate(${compassHeading}deg) scale(1)`;
        arrow.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
      });

      // Click handler
      el.addEventListener('click', () => {
        onPhotoClick(photo);
      });

      // Create popup with photo preview
      const popupContent = document.createElement('div');
      popupContent.style.cssText = `
        display: flex;
        flex-direction: column;
        min-width: 180px;
        max-width: 220px;
      `;

      const thumbnail = document.createElement('img');
      thumbnail.alt = photo.file_name;
      thumbnail.style.cssText = `
        width: 100%;
        height: 120px;
        object-fit: cover;
        border-radius: 4px 4px 0 0;
        margin: -12px -12px 8px -12px;
        background-color: #f0f0f0;
      `;
      // Fetch with auth token and assign blob URL when ready
      fetchAuthThumbnail(photo.uuid, photo.sourceType).then((blobUrl) => {
        thumbnail.src = blobUrl;
        blobUrlsRef.current.push(blobUrl);
      }).catch(() => {
        thumbnail.style.display = 'none';
      });

      const fileName = document.createElement('div');
      const displayTitle = photo.fileName || (photo.sourceType === 'free_photo' ? 'Ảnh tự do' : 'Ảnh thu thập');
      fileName.textContent = displayTitle;
      fileName.style.cssText = `
        font-weight: 500;
        font-size: 13px;
        margin-bottom: 4px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `;

      const date = document.createElement('div');
      date.innerHTML = `${renderToString(<PiCalendarBlank style={{ verticalAlign: '-1px', marginRight: 4 }} />)}${formatPhotoDate(photo.createdAt)}`;
      date.style.cssText = `
        font-size: 11px;
        color: #666;
        margin-bottom: 4px;
        display: flex;
        align-items: center;
      `;

      const direction = document.createElement('div');
      direction.innerHTML = `${renderToString(<PiCompass style={{ verticalAlign: '-1px', marginRight: 4 }} />)}${compassHeading}°`;
      direction.style.cssText = `
        font-size: 11px;
        color: #666;
        display: flex;
        align-items: center;
      `;

      popupContent.appendChild(thumbnail);
      popupContent.appendChild(fileName);
      popupContent.appendChild(date);
      popupContent.appendChild(direction);

      const popup = new maplibregl.Popup({
        offset: 25,
        closeButton: true,
        maxWidth: '220px',
      }).setDOMContent(popupContent);

      // Create marker
      const marker = new maplibregl.Marker({ element: el })
        .setLngLat(coordinates)
        .setPopup(popup)
        .addTo(map.current!);

      markersRef.current.push(marker);
      popupsRef.current.push(popup);
    });

    // Fit bounds to show all markers
    if (photoMarkers.length > 0) {
      const bounds = new maplibregl.LngLatBounds();
      photoMarkers.forEach((marker) => {
        bounds.extend(marker.coordinates);
      });
      
      map.current.fitBounds(bounds, {
        padding: { top: 80, bottom: 50, left: 50, right: 50 },
        maxZoom: 15,
        duration: 1000,
      });
    }
  }, [photoMarkers, mapLoaded, onPhotoClick]);

  const handleFitToPhotos = () => {
    if (!map.current || photoMarkers.length === 0) return;

    const bounds = new maplibregl.LngLatBounds();
    photoMarkers.forEach((marker) => {
      bounds.extend(marker.coordinates);
    });
    
    map.current.fitBounds(bounds, {
      padding: { top: 80, bottom: 50, left: 50, right: 50 },
      maxZoom: 15,
      duration: 1000,
    });
  };

  const handleMyLocation = () => {
    if (!map.current) return;

    if (!navigator.geolocation) {
      message.warning('Trình duyệt không hỗ trợ định vị');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (!map.current) return;
        
        const { longitude, latitude } = position.coords;
        
        // Add a marker for user location
        const userMarkerEl = document.createElement('div');
        userMarkerEl.style.cssText = `
          width: 20px;
          height: 20px;
          background-color: #ff4d4f;
          border: 3px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        `;

        new maplibregl.Marker({ element: userMarkerEl })
          .setLngLat([longitude, latitude])
          .setPopup(
            new maplibregl.Popup({ offset: 15 })
              .setHTML(`<div style="padding: 4px; display: flex; align-items: center; gap: 4px;">${renderToString(<PiMapPin style={{ color: '#ff4d4f' }} />)} Vị trí của bạn</div>`)
          )
          .addTo(map.current);

        map.current.flyTo({
          center: [longitude, latitude],
          zoom: 14,
          duration: 1500,
        });
      },
      (error) => {
        console.error('Error getting location:', error);
        message.error('Không thể lấy vị trí của bạn');
      }
    );
  };

  const handleRefresh = () => {
    handleFitToPhotos();
    message.success('Đã làm mới bản đồ');
  };

  if (photos.length === 0) {
    return (
      <UI_EmptyWrapper>
        <Empty 
          description="Không có ảnh nào để hiển thị trên bản đồ" 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </UI_EmptyWrapper>
    );
  }

  if (photoMarkers.length === 0) {
    return (
      <UI_EmptyWrapper>
        <Empty 
          description={
            <div>
              <p>Không có ảnh nào có thông tin vị trí GPS</p>
              <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
                {photos.length} ảnh được tìm thấy nhưng không có tọa độ GPS hợp lệ
              </p>
            </div>
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </UI_EmptyWrapper>
    );
  }

  return (
    <UI_Wrapper>
      <UI_MapContainer ref={mapContainer} />
      
      <UI_Controls>
        <Space direction="vertical">
          <Button 
            icon={<ExpandOutlined />} 
            onClick={handleFitToPhotos}
            title="Xem tất cả ảnh"
          >
            Xem tất cả
          </Button>
          <Button 
            icon={<AimOutlined />} 
            onClick={handleMyLocation}
            title="Vị trí của tôi"
          >
            Vị trí của tôi
          </Button>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleRefresh}
            title="Làm mới"
          />
        </Space>
      </UI_Controls>

      <UI_PhotoCount>
        <Badge 
          count={photoMarkers.length} 
          showZero 
          style={{ backgroundColor: '#52c41a' }}
        />
        <span style={{ marginLeft: '8px', fontSize: '12px' }}>
          ảnh trên bản đồ
        </span>
      </UI_PhotoCount>
    </UI_Wrapper>
  );
};

const UI_Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const UI_MapContainer = styled.div`
  width: 100%;
  height: 100%;
  
  .maplibregl-popup-content {
    padding: 12px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  .maplibregl-popup-close-button {
    font-size: 20px;
    padding: 0 8px;
    color: #999;
    
    &:hover {
      color: #333;
    }
  }
`;

const UI_Controls = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 1;

  @media (max-width: 768px) {
    top: 8px;
    left: 8px;
    
    button {
      padding: 4px 8px;
      font-size: 12px;
    }
  }
`;

const UI_PhotoCount = styled.div`
  position: absolute;
  bottom: 40px;
  left: 16px;
  background-color: white;
  padding: 8px 12px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  font-size: 13px;
  z-index: 1;

  @media (max-width: 768px) {
    bottom: 30px;
    left: 8px;
    font-size: 11px;
    padding: 6px 10px;
  }
`;

const UI_EmptyWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: #f5f5f5;
`;
